import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import io from 'socket.io-client';
import api from '../config/axiosConfig';
import messagerieSocket from '../socket/messagerieSocket';

export const SocketContext = createContext();

export const SocketProvider = ({ children }) => {
  const [socket, setSocket]           = useState(null);
  const [profile, setProfile]         = useState({});
  const [subscription, setSubscription] = useState(null);
  const socketRef = useRef(null);
  

  const fetchData = useCallback(async () => {
    try {
      const profileResponse      = await api.get('/users/me');
      const subscriptionResponse = await api.get('/users/me/subscription');
      setProfile(profileResponse.data);
      setSubscription(subscriptionResponse.data || null);
      return profileResponse.data; // retourne le profil pour l'init socket
    } catch (error) {
      console.error('Error fetching data:', error);
      return null;
    }
  }, []);

  // Connexion socket APRÈS avoir le profil — compatible dev et prod
  const initSocket = useCallback((userId) => {
    // Évite les doubles connexions
    if (socketRef.current?.connected) return;
    if (socketRef.current) socketRef.current.disconnect();

  const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 
                    (process.env.NODE_ENV === "production" 
                      ? "https://api.pesacore.fr"
                      : "https://localhost:5000");

    const newSocket = io(SOCKET_URL, {
      withCredentials: true,
      transports: process.env.NODE_ENV === 'development' ? ['polling'] : ['polling', 'websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      // pas de secure: true ici, c'est géré par l'URL https://
    });

    socketRef.current = newSocket;
    setSocket(newSocket);

    newSocket.on('connect', () => {
      console.log('[SOCKET] Connecté:', newSocket.id);
      if (userId) newSocket.emit('join-user-room', userId);
    });

    newSocket.on('connect_error', (err) => {
      console.error('[SOCKET] Erreur connexion:', err.message);
    });

    newSocket.on('disconnect', (reason) => {
      console.log('[SOCKET] Déconnecté:', reason);
    });

    newSocket.on('subscriptionUpdated', ({ subscriptionPlan, subscriptionId }) => {
      setSubscription({ plan: subscriptionPlan, subscriptionId });
    });

    if (process.env.NODE_ENV === 'development') {
      newSocket.onAny((eventName, ...args) => {
        console.log(`[SOCKET DEBUG] ${eventName}`, args);
      });
    }

    return newSocket;
  }, []);

  // Fetch profil d'abord, puis init socket avec l'userId
  useEffect(() => {
    fetchData().then(profileData => {
      if (profileData?._id) {
        initSocket(profileData._id);
        
        // === NOUVEAU : Initialisation du socket messagerie ===
        messagerieSocket.setUserId(profileData._id);
        messagerieSocket.connect();
      }
    });

    return () => {
      socketRef.current?.disconnect();
      messagerieSocket.cleanup();   // nettoyage propre
    };
  }, [fetchData]);
  
  useEffect(() => {
    if (profile?._id) {
      console.log(`[SOCKET] Profil mis à jour → re-join pour user ${profile._id}`);
      
      // Pour le socket global
      if (socketRef.current) {
        socketRef.current.emit('join-user-room', profile._id);
      }
      
      // Pour le socket messagerie
      messagerieSocket.setUserId(profile._id);
    }
  }, [profile?._id]);

  const contextValue = {
    socket,
    setSocket,
    profile,
    setProfile,
    subscription,
    setSubscription,
    fetchData,
    messagerieSocket,
  };

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) throw new Error('useSocket must be used within a SocketProvider');
  return context;
};